import React from "react";
import { useTranslation } from "react-i18next";
import { isLastIncompleteAllocation } from "./utils";
import { allocation, ResourceTypes } from "@planzone/dto";
import {
  useAllocateResources,
  useRemoveAllocation,
} from "@planzone/view-models";
import { SelectList } from "@common/selectList";
import { AddAllocationIcon } from "@common/assets/addAllocationIcon";
import { ResourceAllocationMenu } from "@common/resourceAllocationMenu";
import { Header } from "./header";
import { Allocation } from "./allocation";
import { Props } from "./types";
import styles from "./styles.scss";

function Allocations({ task, allocations, project, readOnly }: Props) {
  const { t } = useTranslation();
  const { isRequestPending, allocateResources } = useAllocateResources(task);
  const { removeAllocation } = useRemoveAllocation({ ...allocation, uuid: "" });

  return (
    <div className={styles.allocations}>
      <Header
        action={
          !readOnly ? (
            <SelectList
              rootClassName={styles.addAllocation}
              listClassName={styles.list}
            >
              {({ isOpen, select }) => ({
                input: (
                  <div className={styles.iconBox}>
                    <AddAllocationIcon className={styles.icon} />
                  </div>
                ),
                list: (
                  <ResourceAllocationMenu
                    allocatedResources={task.allocations.map(
                      (allocation) => allocation.resource,
                    )}
                    select={(resources) =>
                      !isRequestPending &&
                      allocateResources({
                        task,
                        allocations: resources.map((resource) => ({
                          ...allocation,
                          resource,
                        })),
                        project,
                      })
                    }
                    unSelect={(resources) =>
                      resources.forEach(({ uuid }) => {
                        const allocation = allocations.find(
                          (allocation) => allocation.resource.uuid === uuid,
                        );

                        allocation && removeAllocation(allocation);
                      })
                    }
                    openedViews={["resources"]}
                  />
                ),
              })}
            </SelectList>
          ) : undefined
        }
      />
      <div className={styles.main}>
        {allocations.length > 0 ? (
          [...allocations]
            .sort((a, b) => a.resource.name.localeCompare(b.resource.name))
            .sort((a, b) => {
              const priority = {
                [ResourceTypes.HUMAN]: 1,
                [ResourceTypes.VIRTUAL]: 2,
                [ResourceTypes.MATERIAL]: 3,
              };

              return priority[a.resource.type] - priority[b.resource.type];
            })
            .map((allocation) => (
              <div key={allocation.uuid} className={styles.item}>
                <Allocation
                  allocation={allocation}
                  isLastIncompleteAllocation={isLastIncompleteAllocation(
                    allocations,
                    allocation,
                  )}
                  task={task}
                  project={project}
                />
              </div>
            ))
        ) : (
          <div className={styles.empty}>
            <p className={styles.emptyText}>
              {t("taskPage.allocations.placeholder")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export { Allocations };
